import type {
  DocusignSignatureResponse,
  ProposalSignatureMethod,
  ProposalSignatureMethodResponse,
} from '@/services/proposals/types';
import {
  mapEnvelopeStatusLabel,
  mapRecipientStatusLabel,
} from '@/features/proposals/utils/docusign-signature-status.util';

export { mapEnvelopeStatusLabel, mapRecipientStatusLabel };

const ENVELOPE_ACTIVE_STATUSES = new Set(['sent', 'delivered']);

const DIGITAL_BLOCKED_REASON =
  'Indisponível após escolher assinatura digital para esta proposta.';

const PHYSICAL_BLOCKED_REASON =
  'Indisponível após escolher assinatura física para esta proposta.';

export function resolveCapitalGlobalLockedSignatureMethod(input: {
  signatureMethod: ProposalSignatureMethodResponse | null | undefined;
  signature: DocusignSignatureResponse | null | undefined;
}): ProposalSignatureMethod | null {
  if (input.signatureMethod?.locked && input.signatureMethod.method) {
    return input.signatureMethod.method;
  }

  if (input.signature) {
    return 'digital';
  }

  return null;
}

export function isPhysicalSignatureMethodLocked(
  lockedMethod: ProposalSignatureMethod | null | undefined
): boolean {
  return lockedMethod === 'physical';
}

export function isDigitalSignatureMethodLocked(
  lockedMethod: ProposalSignatureMethod | null | undefined
): boolean {
  return lockedMethod === 'digital';
}

export function isDigitalEnvelopeBlockingPhysical(
  signature: DocusignSignatureResponse | null | undefined,
  lockedMethod?: ProposalSignatureMethod | null
): boolean {
  if (isPhysicalSignatureMethodLocked(lockedMethod)) {
    return false;
  }

  if (isDigitalSignatureMethodLocked(lockedMethod)) {
    return true;
  }

  if (!signature) return false;

  const { status, failureSubtype } = signature;

  if (status === 'completed') return true;
  if (status === 'declined' || status === 'voided') return false;
  if (status === 'failed') {
    return failureSubtype !== 'failed_create';
  }

  return ENVELOPE_ACTIVE_STATUSES.has(status);
}

export function isDigitalEnvelopeBlockingStep4(
  signature: DocusignSignatureResponse | null | undefined
): boolean {
  if (!signature) return false;

  const { status, failureSubtype } = signature;

  if (status === 'completed') return false;
  if (status === 'declined' || status === 'voided') return false;
  if (status === 'failed') {
    return failureSubtype !== 'failed_create';
  }

  return ENVELOPE_ACTIVE_STATUSES.has(status);
}

export {
  canRetryDocusignSignature,
  canSyncDocusignSignature,
} from '@/features/proposals/utils/docusign-signature-status.util';

export function canStartDigitalSignature(
  signature: DocusignSignatureResponse | null | undefined,
  lockedMethod?: ProposalSignatureMethod | null
): boolean {
  if (isPhysicalSignatureMethodLocked(lockedMethod)) {
    return false;
  }

  if (isDigitalSignatureMethodLocked(lockedMethod)) {
    return canStartDigitalSignatureWithoutLock(signature);
  }

  return canStartDigitalSignatureWithoutLock(signature);
}

function canStartDigitalSignatureWithoutLock(
  signature: DocusignSignatureResponse | null | undefined
): boolean {
  if (!signature) return true;
  if (signature.status === 'completed') return false;
  if (signature.status === 'declined' || signature.status === 'voided') {
    return true;
  }
  if (
    signature.status === 'failed' &&
    signature.failureSubtype === 'failed_create'
  ) {
    return true;
  }
  return false;
}

export function isPhysicalSignatureButtonBlocked(input: {
  lockedMethod: ProposalSignatureMethod | null | undefined;
  signature: DocusignSignatureResponse | null | undefined;
  hasSignedProposal: boolean;
}): boolean {
  if (
    isDigitalEnvelopeBlockingPhysical(input.signature, input.lockedMethod)
  ) {
    return true;
  }

  if (
    isPhysicalSignatureMethodLocked(input.lockedMethod) &&
    input.hasSignedProposal
  ) {
    return true;
  }

  return false;
}

export function getPhysicalSignatureBlockedReason(
  lockedMethod: ProposalSignatureMethod | null | undefined,
  signature: DocusignSignatureResponse | null | undefined,
  hasSignedProposal = false
): string | undefined {
  if (
    isPhysicalSignatureMethodLocked(lockedMethod) &&
    hasSignedProposal
  ) {
    return 'Proposta assinada já enviada para esta proposta.';
  }

  if (isPhysicalSignatureMethodLocked(lockedMethod)) {
    return undefined;
  }

  if (!isDigitalEnvelopeBlockingPhysical(signature, lockedMethod)) {
    return undefined;
  }

  return isDigitalSignatureMethodLocked(lockedMethod)
    ? DIGITAL_BLOCKED_REASON
    : 'Indisponível enquanto houver uma assinatura digital em andamento ou concluída.';
}

export function getDigitalSignatureBlockedReason(
  lockedMethod: ProposalSignatureMethod | null | undefined
): string | undefined {
  if (!isPhysicalSignatureMethodLocked(lockedMethod)) {
    return undefined;
  }

  return PHYSICAL_BLOCKED_REASON;
}
